import { isIndirectTicket } from 'lib/constants/laborTicketTypes';

// A part adjustment type that is neither scrap nor correction is treated by
// the API as a manual part-count adjustment (raw part-count stream) rather
// than a reject. Only the built-in (null-company) reason qualifies here:
// company-created reasons are always scrap:true via the API, and a legacy
// company row with odd flags must keep today's reject behavior.
const isManualAdjustmentType = (partAdjustmentType) => {
  return (
    !!partAdjustmentType &&
    partAdjustmentType.companyId == null &&
    !partAdjustmentType.scrap &&
    !partAdjustmentType.correction
  );
};

// Company-created reasons are always shown; global (null-company) reasons are
// limited to the manual-adjustment type, and only when the feature is enabled.
const filterPartAdjustmentTypes = (
  partAdjustmentTypes,
  includeManualAdjustment
) => {
  return partAdjustmentTypes.filter((partAdjustmentType) => {
    if (partAdjustmentType.companyId != null) return true;
    return (
      includeManualAdjustment && isManualAdjustmentType(partAdjustmentType)
    );
  });
};

const MINUTE_MS = 60 * 1000;

// The ERP stores labor ticket times at minute precision and rounds a sub-minute clock-in up to
// the next minute, so a ticket's rejects belong to it from that rounded minute on. The labor
// ticket form and ERP quality reconciliation both use this as the start of the ticket's window.
const getLaborTicketRejectWindowStartMs = (clockIn) => {
  const clockInMs = Date.parse(clockIn);
  if (!Number.isFinite(clockInMs)) return null;
  return Math.ceil(clockInMs / MINUTE_MS) * MINUTE_MS;
};

const isOpenLaborTicket = (laborTicket) => {
  return (
    !!laborTicket?.clockIn &&
    !laborTicket.clockOut &&
    laborTicket.state !== 'CLOSED'
  );
};

// Rejects can be recorded only during an open job labor ticket. Indirect tickets (kiosk labor)
// don't report parts, so rejects recorded during one would never reach the ERP.
const isRejectableLaborTicket = (laborTicket) => {
  return isOpenLaborTicket(laborTicket) && !isIndirectTicket(laborTicket);
};

// Rejects recorded during an open labor ticket are counted toward it on the labor ticket form.
// Stamp them explicitly, no earlier than the ticket's rounded clock-in, so they stay inside its
// window. (The server's default stamp is truncated to the second minus 1ms, which would put a
// reject made just after the rounded clock-in before it.)
const getLaborTicketRejectTime = (laborTicket, nowMs) => {
  if (!isRejectableLaborTicket(laborTicket)) return undefined;
  const windowStartMs = getLaborTicketRejectWindowStartMs(laborTicket.clockIn);
  if (windowStartMs === null) return undefined;
  return new Date(Math.max(windowStartMs, nowMs)).toISOString();
};

const buildRejectPartsPayload = ({
  parts,
  category,
  notes,
  type,
  removeFromGoodParts,
  machineId,
  adjustmenttime,
}) => {
  const timing = adjustmenttime ? { adjustmenttime } : {};

  if (isManualAdjustmentType(category)) {
    return {
      count: -parts,
      machine_id: machineId,
      category_id: category.id,
      message: notes || null,
      ...timing,
    };
  }

  return {
    scrapcount: parts,
    machine_id: machineId,
    count: removeFromGoodParts ? 0 : parts,
    reject_behavior: type,
    category_id: category.id,
    message: notes || null,
    ...timing,
  };
};

export {
  isManualAdjustmentType,
  filterPartAdjustmentTypes,
  getLaborTicketRejectWindowStartMs,
  isOpenLaborTicket,
  isRejectableLaborTicket,
  getLaborTicketRejectTime,
  buildRejectPartsPayload,
};
